import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import SectionHeading from '@/components/feature/SectionHeading';
import { movies } from '@/mocks/movies';
import { useFavorites } from '@/hooks/useFavorites';
import { useSeo } from '@/hooks/useSeo';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === id);
  const { toggleFavorite, isFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  useSeo(
    movie ? `${movie.title}详情介绍 - 十八禁影视` : '影视详情 - 十八禁影视',
    movie
      ? `${movie.title}（${movie.year}·${movie.region}）影视详情介绍：${movie.synopsis.slice(0, 60)}，并附同类型或同导演的推荐作品。`
      : '十八禁影视作品详情介绍页。',
  );

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter(
        (m) =>
          m.id !== movie.id &&
          (m.director === movie.director || m.genres.some((g) => movie.genres.includes(g))),
      )
      .slice(0, 6);
  }, [movie]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2200);
  };

  if (!movie) {
    return (
      <div className="w-full pt-16 md:pt-20 px-4 md:px-8 py-24 text-center">
        <span className="mx-auto w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center mb-5">
          <i className="ri-emotion-sad-line text-2xl text-foreground-500" />
        </span>
        <h1 className="font-heading text-xl md:text-2xl text-foreground-950 font-bold">未找到该影视作品</h1>
        <p className="mt-3 text-sm text-foreground-600">链接可能已失效，或该作品已从站内片库移除。</p>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm rounded-md px-5 py-2.5 whitespace-nowrap cursor-pointer transition-colors"
        >
          返回分类页
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(movie.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast('页面链接已复制，可粘贴分享给朋友');
    } catch {
      showToast('复制失败，请手动复制地址栏链接');
    }
  };

  const handleWatch = () => {
    showToast('本站不提供在线播放，仅作影视信息与观看引导');
  };

  return (
    <div className="w-full pt-16 md:pt-20">
      {/* 标题及封面大图 */}
      <section className="relative w-full">
        <div className="absolute inset-0 h-[360px] md:h-[460px] overflow-hidden">
          <img
            src={movie.backdrop}
            alt={`${movie.title} 大图`}
            title={`${movie.title} 详情介绍 - 十八禁影视`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/30" />
        </div>

        <div className="relative w-full px-4 md:px-8 pt-24 md:pt-32 pb-8">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-600 mb-6 flex-wrap">
            <Link to="/" className="hover:text-primary-400 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/category" className="hover:text-primary-400 transition-colors">
              影视分类
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800 truncate max-w-[160px]">{movie.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">
            <div className="w-36 sm:w-44 md:w-52 shrink-0 rounded-lg overflow-hidden border border-background-200/70 bg-background-300">
              <img
                src={movie.poster}
                alt={`${movie.title} 封面`}
                title={`${movie.title} - 十八禁影视大全`}
                className="w-full aspect-[2/3] object-top object-cover"
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className="bg-primary-500 text-foreground-950 text-xs px-2.5 py-1 rounded-md font-label">
                  {movie.type === 'movie' ? '电影' : '电视剧'}
                </span>
                <span className="flex items-center gap-1 text-sm text-accent-400 font-bold">
                  <i className="ri-star-fill" />
                  {movie.rating.toFixed(1)}
                </span>
                <span className="text-xs text-foreground-600">站内热度 {(movie.views / 10000).toFixed(1)}万</span>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 font-bold">{movie.title}</h1>

              <div className="mt-4 flex flex-wrap gap-2">
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category?genre=${encodeURIComponent(g)}`}
                    className="text-xs text-foreground-700 bg-background-100 border border-background-200 px-2.5 py-1 rounded-md hover:border-primary-400/60 hover:text-primary-400 transition-colors"
                  >
                    {g}
                  </Link>
                ))}
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    const now = toggleFavorite(movie.id);
                    showToast(now ? '已加入我的收藏' : '已取消收藏');
                  }}
                  className={`inline-flex items-center gap-2 text-sm rounded-md px-5 py-2.5 whitespace-nowrap cursor-pointer border transition-colors ${
                    favorited
                      ? 'bg-accent-500 border-accent-500 text-background-50'
                      : 'bg-background-100 border-background-300/60 text-foreground-900 hover:border-accent-400'
                  }`}
                >
                  <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'} />
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 bg-background-100 border border-background-300/60 hover:border-primary-400/60 text-foreground-900 text-sm rounded-md px-5 py-2.5 whitespace-nowrap cursor-pointer transition-colors"
                >
                  <i className="ri-share-forward-line" />
                  分享
                </button>
                <button
                  type="button"
                  onClick={handleWatch}
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm rounded-md px-5 py-2.5 whitespace-nowrap cursor-pointer transition-colors"
                >
                  <i className="ri-play-circle-line text-lg" />
                  观看指引
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 pb-14 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* 影片信息 */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-6">
              <h2 className="font-heading text-lg text-foreground-950 font-bold mb-4 flex items-center gap-2">
                <span className="w-1 h-5 bg-primary-500 rounded-full" />
                影片简介
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{movie.synopsis}</p>
              <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                本作以{movie.genres.join('、')}为核心类型，由{movie.director}执导，
                {movie.cast.join('、')}联袂出演。影片在情感刻画与氛围营造上均有较强作者表达，
                属于面向成年观众的{movie.type === 'movie' ? '电影' : '剧集'}作品。
              </p>
            </div>

            <div className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-6">
              <h2 className="font-heading text-lg text-foreground-950 font-bold mb-4 flex items-center gap-2">
                <span className="w-1 h-5 bg-accent-500 rounded-full" />
                基础信息
              </h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                {[
                  { label: '片名', value: movie.title },
                  { label: '类型', value: movie.genres.join(' / ') },
                  { label: '上映年份', value: String(movie.year) },
                  { label: '地区', value: movie.region },
                  { label: '导演', value: movie.director },
                  { label: '主演', value: movie.cast.join(' / ') },
                ].map((row) => (
                  <div key={row.label} className="flex gap-3">
                    <dt className="text-foreground-500 shrink-0 w-16">{row.label}</dt>
                    <dd className="text-foreground-800 min-w-0">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-5 text-xs text-foreground-500">
                注：以上片名、导演与演员均为演示数据，仅用于页面效果展示。
              </p>
            </div>

            {/* 播放提示区 */}
            <div className="bg-primary-500/10 border border-primary-500/25 rounded-lg p-5 md:p-6">
              <h2 className="font-heading text-lg text-foreground-950 font-bold mb-3 flex items-center gap-2">
                <i className="ri-information-line text-primary-400" />
                播放提示
              </h2>
              <p className="text-sm text-foreground-700 leading-relaxed">
                本站不提供在线播放，仅供影视信息及观看引导。你可以在以下示意入口了解作品的正式观看渠道，
                支持正版内容，共同维护良好的影视环境。
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                {['正版流媒体', '线下影院', '官方平台'].map((source) => (
                  <button
                    key={source}
                    type="button"
                    onClick={handleWatch}
                    className="inline-flex items-center gap-2 bg-background-100 border border-background-300/60 text-foreground-700 text-xs md:text-sm rounded-md px-4 py-2 whitespace-nowrap cursor-pointer hover:border-primary-400/60 transition-colors"
                  >
                    <i className="ri-external-link-line" />
                    {source}（示意）
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 侧栏快捷 */}
          <aside className="space-y-4">
            <div className="bg-background-100 border border-background-200/70 rounded-lg p-5">
              <h2 className="font-heading text-base text-foreground-950 font-bold mb-4">分类快捷入口</h2>
              <div className="flex flex-col gap-2">
                <Link
                  to={`/category?type=${movie.type}`}
                  className="flex items-center justify-between px-4 py-3 rounded-md bg-background-50 border border-background-200 hover:border-primary-400/60 transition-colors text-sm text-foreground-800"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-stack-line text-accent-400" />
                    更多{movie.type === 'movie' ? '电影' : '电视剧'}
                  </span>
                  <i className="ri-arrow-right-s-line" />
                </Link>
                <Link
                  to={`/category?region=${encodeURIComponent(movie.region)}`}
                  className="flex items-center justify-between px-4 py-3 rounded-md bg-background-50 border border-background-200 hover:border-primary-400/60 transition-colors text-sm text-foreground-800"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-earth-line text-accent-400" />
                    {movie.region}作品
                  </span>
                  <i className="ri-arrow-right-s-line" />
                </Link>
                <Link
                  to="/special"
                  className="flex items-center justify-between px-4 py-3 rounded-md bg-background-50 border border-background-200 hover:border-primary-400/60 transition-colors text-sm text-foreground-800"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-bookmark-3-line text-accent-400" />
                    相关专题
                  </span>
                  <i className="ri-arrow-right-s-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>

        {/* 相关推荐 */}
        {related.length > 0 && (
          <section className="mt-12 md:mt-16">
            <SectionHeading
              eyebrow="YOU MAY ALSO LIKE"
              title="相关推荐"
              description="根据类型与导演的相似度匹配，为你推荐可能感兴趣的作品。"
              moreTo="/category"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {related.map((m, i) => (
                <MovieCard key={m.id} movie={m} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>

      {toast && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-24 z-50 bg-background-200 border border-background-300/60 text-foreground-950 text-sm px-5 py-3 rounded-md animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}